import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { completeFollowUp, getStats, listResponses, type NpsCategory } from '@/lib/api/nps.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatDateTime } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

const categoryColors: Record<NpsCategory, string> = {
  promoter: 'bg-emerald-500',
  passive: 'bg-amber-400',
  detractor: 'bg-red-500',
};

function categoryLabel(cat: NpsCategory | null) {
  if (cat === 'promoter') return 'Promoter';
  if (cat === 'passive') return 'Passive';
  if (cat === 'detractor') return 'Detractor';
  return 'Unknown';
}

function BreakdownBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">
          {count} ({pct}%)
        </span>
      </div>
      <div className="h-3 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function NpsReportPage() {
  const qBuilt = useReportQueryParams();
  const queryClient = useQueryClient();
  const [followUpNotes, setFollowUpNotes] = useState<Record<string, string>>({});

  const statsQ = useQuery({
    queryKey: ['nps-stats', qBuilt],
    queryFn: () => getStats(qBuilt),
  });

  const responsesQ = useQuery({
    queryKey: ['nps-responses', qBuilt],
    queryFn: () => listResponses({ ...qBuilt, pageSize: 50 }),
  });

  const followUpMut = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      completeFollowUp(id, { followUpCompleted: true, followUpNotes: notes }),
    onSuccess: () => {
      toastSuccess('Follow-up marked complete');
      queryClient.invalidateQueries({ queryKey: ['nps-stats'] });
      queryClient.invalidateQueries({ queryKey: ['nps-responses'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const stats = statsQ.data;
  const responses = responsesQ.data?.data ?? [];
  const totalCat = (stats?.promoters ?? 0) + (stats?.passives ?? 0) + (stats?.detractors ?? 0);

  if (statsQ.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading NPS report…</div>;
  }
  if (statsQ.isError) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load NPS report.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">NPS Score</p>
          <p className="mt-1 text-4xl font-black text-primary">{stats?.npsScore ?? 0}</p>
          <p className="mt-1 text-xs text-slate-500">{stats?.totalResponses ?? 0} responses</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Avg Score</p>
          <p className="mt-1 text-4xl font-black text-slate-950">{stats?.avgScore ?? 0}</p>
          <p className="mt-1 text-xs text-slate-500">Out of 10</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Response Rate</p>
          <p className="mt-1 text-4xl font-black text-slate-950">{stats?.responseRate ?? 0}%</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Follow-ups Needed</p>
          <p className="mt-1 text-4xl font-black text-red-600">{stats?.followUpsRequired ?? 0}</p>
          <p className="mt-1 text-xs text-slate-500">{stats?.googleReviewsSent ?? 0} Google reviews sent</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Score Breakdown</h3>
        <div className="mt-4 space-y-4">
          <BreakdownBar
            label="Promoters (9–10)"
            count={stats?.promoters ?? 0}
            total={totalCat}
            color={categoryColors.promoter}
          />
          <BreakdownBar
            label="Passives (7–8)"
            count={stats?.passives ?? 0}
            total={totalCat}
            color={categoryColors.passive}
          />
          <BreakdownBar
            label="Detractors (0–6)"
            count={stats?.detractors ?? 0}
            total={totalCat}
            color={categoryColors.detractor}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-sm font-bold text-slate-950">Recent Responses</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {responses.map((r) => (
            <div key={r.id} className="px-5 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-2xl font-black text-slate-950">{r.score ?? '—'}</span>
                    {r.npsCategory ? (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold text-white ${categoryColors[r.npsCategory]}`}
                      >
                        {categoryLabel(r.npsCategory)}
                      </span>
                    ) : null}
                    {r.followUpRequired && !r.followUpCompleted ? (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        Needs follow-up
                      </span>
                    ) : null}
                    {r.followUpCompleted ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Follow-up done
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-800">
                    {r.customerName ?? 'Unknown customer'}
                    {r.ticketNumber ? ` · ${r.ticketNumber}` : ''}
                    {r.deviceName ? ` · ${r.deviceName}` : ''}
                  </p>
                  {r.feedback ? (
                    <p className="mt-2 text-sm text-slate-600">&ldquo;{r.feedback}&rdquo;</p>
                  ) : null}
                  <p className="mt-1 text-xs text-slate-400">
                    {r.respondedAt ? formatDateTime(r.respondedAt) : 'No response date'}
                  </p>
                </div>
                {r.followUpRequired && !r.followUpCompleted && r.npsCategory === 'detractor' ? (
                  <div className="w-full max-w-xs space-y-2 sm:w-auto">
                    <textarea
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Follow-up notes (optional)"
                      rows={2}
                      value={followUpNotes[r.id] ?? ''}
                      onChange={(e) =>
                        setFollowUpNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="w-full rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      disabled={followUpMut.isPending}
                      onClick={() =>
                        followUpMut.mutate({ id: r.id, notes: followUpNotes[r.id] || undefined })
                      }
                    >
                      Mark Complete
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {!responses.length ? (
            <p className="p-8 text-center text-slate-500">No NPS responses in this period.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
